import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {getNoOfGridItems} from '@Components/base-grid';
import {setHasLanguageSelectorJustClosed} from '@Panels/ai-subtitle-panel/ai-subtitle-reducer';
import {AddAiItemPopOverContentState, AI_IMAGE_PANEL_ID, AI_SUBTITLE_PANEL_ID, AI_VOICE_PANEL_ID} from './add-ai-item-button.types';
import styles from './add-ai-item-button.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {GA4EventName, GA4EventParamName, GA4EventSources, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {AiSubtitleMenuOpenedFrom, getAiSubtitlesPanelHeightClass} from '@Libraries/ai-transcript.library';
import {getUserId, isUserPremium} from '@Libraries/user.library';
import {AddAiItemDropdownPopupContent} from '@Components/add-ai-item-button/components/add-ai-item-dropdown-popup-content/add-ai-item-dropdown-popup-content';
import {AddItemButton} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-button';
import {getGridIdForPanel} from '@Libraries/text-to-image-library';
import {AiVoicePanelDataKey, AiVoicePanelGenerationState} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import {showLoading} from '@Libraries/loading-toast-library';
import {getLoadingKeyForAiVoice} from '@Panels/ai-voice-panel/ai-voice-panel.library';
import {AddAiItemPopUpSubPanel} from '@Components/add-ai-item-button/components/add-ai-item-dropdown-popup-content/add-ai-item-dropdown-popup-content.types';
import {getHasUserSeenAiVoice, markUserHasSeenAiVoice} from '@Libraries/text-to-speech.library';
import {ADD_ITEM_BAR_POPUP_OFFSET, type AddItemOptionsProps} from '@Components/poster-editor/components/poster-add-item-bar/poster-add-item-bar.types';
import {FeatureToShowOnLoad} from '@Components/poster-editor/poster-editor.types';
import {updateFeatureToShowOnLoad, updateRemoveBgMode} from '@Components/poster-editor/poster-editor-reducer';
import {MediaTypePrompt} from '@Panels/ai-image-panel/ai-image-panel.types';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import type {CanvasEvents} from '@postermywall/fabricjs-2';
import {changeCanvasCursor} from '@Components/poster-editor/library/poster-editor-library';
import useWindowSize from '@Hooks/useWindowSize';

const AI_PANEL_FULL_HEIGHT_POPUP_OFFSET = 72;
const ARROW_TIP_CENTERING_OFFSET = 15;
const PADDING_AND_NAV_HEIGHT = 88;

export function AddAiItemButton({arrowClasses = '', popupClassName = '', ...props}: AddItemOptionsProps): ReactElement {
  const dispatch = useAppDispatch();
  const {windowHeight} = useWindowSize();
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());
  const [showAiVoiceAsNewFeature, setShowAiVoiceAsNewFeature] = useState<boolean>(false);
  const [isOpened, setIsOpened] = useState(false);
  const animatePopup = useRef(false);
  const [dropdownContentState, setDropdownContentState] = useState(AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN);
  const numberOfGridItemsInAiImageGrid = useAppSelector((state) => {
    return getNoOfGridItems(state, getGridIdForPanel(AI_IMAGE_PANEL_ID));
  });
  const aiSubtitleReduxState = useAppSelector((state) => {
    return state.aiSubtitle;
  });
  const aiSubtitleState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiSubtitles;
  });
  const hasUserMediaPanelJustClosed = useAppSelector((state) => {
    return state.panels.panelHashmap['user-media-modal-panel'];
  });
  const hasLanguageSelectorJustClosed = useAppSelector((state) => {
    return state.aiSubtitle.aiSubtitlesPanel.hasLanguageSelectorJustClosed;
  });
  const aiVoicePanelGenerationState = useAppSelector((state): AiVoicePanelGenerationState | undefined => {
    return state.panels.panelHashmap[AI_VOICE_PANEL_ID]?.data?.[AiVoicePanelDataKey.GENERATION_STATE] as AiVoicePanelGenerationState;
  });
  const featureToShowOnLoad = useAppSelector((state) => {
    return state.posterEditor.featureToShowOnLoad;
  });
  const [aiImageMediaType, setAiImageMediaType] = useState<MediaTypePrompt>(MediaTypePrompt.PHOTO);
  const [arrowOffset, setArrowOffset] = useState(0);

  useEffect(() => {
    if (!isOpened) {
      return undefined;
    }

    const element = document.getElementById('add-ai-item-pop-over');
    if (!element) {
      return undefined;
    }

    const updateArrowOffset = (): void => {
      const rect = element.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      setArrowOffset(centerY - AI_PANEL_FULL_HEIGHT_POPUP_OFFSET - ARROW_TIP_CENTERING_OFFSET);
    };

    updateArrowOffset();

    const scrollableParent = element.parentElement;
    if (!scrollableParent) {
      return undefined;
    }

    scrollableParent.addEventListener('scroll', updateArrowOffset);
    return (): void => {
      scrollableParent.removeEventListener('scroll', updateArrowOffset);
    };
  }, [windowHeight, dropdownContentState, numberOfGridItemsInAiImageGrid, isOpened]);

  useEffect(() => {
    if (featureToShowOnLoad === FeatureToShowOnLoad.AI_SUBTITLE) {
      setIsOpened(true);
      setDropdownContentState(AddAiItemPopOverContentState.AI_SUBTITLES);
    } else if (featureToShowOnLoad === FeatureToShowOnLoad.AI_VOICE) {
      setIsOpened(true);
      setDropdownContentState(AddAiItemPopOverContentState.AI_VOICE);
    } else if (featureToShowOnLoad === FeatureToShowOnLoad.AI_IMAGE) {
      setIsOpened(true);
      setDropdownContentState(AddAiItemPopOverContentState.AI_IMAGE);
    } else if (featureToShowOnLoad === FeatureToShowOnLoad.AI_TEXT_ART) {
      setAiImageMediaType(MediaTypePrompt.TEXT_ART);
      setIsOpened(true);
      setDropdownContentState(AddAiItemPopOverContentState.AI_IMAGE);
      dispatch(updateFeatureToShowOnLoad(FeatureToShowOnLoad.NONE));
    } else if (featureToShowOnLoad === FeatureToShowOnLoad.AI_REMOVE_BG) {
      setIsOpened(true);
    }
  }, [featureToShowOnLoad]);

  const getSelector = (): ReactElement<HTMLDivElement> => {
    return (
      <AddItemButton
        boldText
        isDisabled={false}
        isSelected={isOpened}
        id="toolbar-ai-images"
        text={window.i18next.t('pmwjs_ai')}
        icon="icon-sparkle"
        className={isOpened ? styles.selectedButton : undefined}
        textClasses={styles.selectorText}
        iconClasses={styles.selectorIcon}
      />
    );
  };

  const getNewFeatureMarkers = (): AddAiItemPopUpSubPanel[] => {
    const newFeatureMarkers: AddAiItemPopUpSubPanel[] = [];
    if (showAiVoiceAsNewFeature) {
      newFeatureMarkers.push(AddAiItemPopUpSubPanel.AI_VOICE);
    }
    return newFeatureMarkers;
  };

  const onSubPanelBack = (): void => {
    switch (dropdownContentState) {
      case AddAiItemPopOverContentState.AI_IMAGE:
      case AddAiItemPopOverContentState.AI_SUBTITLES:
      case AddAiItemPopOverContentState.AI_VOICE:
        setDropdownContentState(AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN);
        break;
      default:
        break;
    }
  };

  const onDone = (): void => {
    setIsOpened(false);
    setDropdownContentState(AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN);
  };

  const onClickAiSubtitlesOption = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.AI_SUBTITLE_CLICK_MENU);
    setDropdownContentState(AddAiItemPopOverContentState.AI_SUBTITLES);
  };

  const onClickAiImagesOption = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_MENU_OPEN, {
      [GA4EventParamName.MENU_OPENED_FROM]: AiSubtitleMenuOpenedFrom.ADD_AI_OPTION,
      [GA4EventParamName.SOURCE]: 'PosterAddItemBar',
    });
    setAiImageMediaType(MediaTypePrompt.PHOTO);
    setDropdownContentState(AddAiItemPopOverContentState.AI_IMAGE);
  };

  const onClickAiVoiceOption = (): void => {
    void markUserHasSeenAiVoice();
    setShowAiVoiceAsNewFeature(false);
    setDropdownContentState(AddAiItemPopOverContentState.AI_VOICE);
  };

  const unselectItems = (): void => {
    window.posterEditor?.whiteboard?.clearSelection();
  };

  const onRemoveBgOption = (event?: CanvasEvents['mouse:move'] | MouseEvent | KeyboardEvent): void => {
    if (!event?.target) {
      showMessageGrowl({
        text: window.i18next.t('pmwjs_select_image_or_video_to_remove_bg'),
        type: GROWL_TYPE.NEUTRAL,
      });
    }
    dispatch(updateRemoveBgMode(false));
    const canvas = window.posterEditor?.whiteboard?.getCurrentPage().fabricCanvas;
    canvas?.off('mouse:down:before', unselectItems);
  };

  const onClickAiRemoveBgOption = (): void => {
    const items = window.posterEditor?.whiteboard?.getCurrentPage().items;
    if (items?.getItems().length) {
      const canvas = window.posterEditor?.whiteboard?.getCurrentPage().fabricCanvas;
      canvas?.on('mouse:down:before', unselectItems);
      changeCanvasCursor('images/postermaker/remove-bg.png', onRemoveBgOption);
      window.PMW.redux.store.dispatch(updateRemoveBgMode(true));
      trackPosterBuilderGA4Events(GA4EventName.REMOVE_BACKGROUND, {[GA4EventParamName.SOURCE]: GA4EventSources.LEFT_TOOLBAR});
    } else {
      showMessageGrowl({
        text: window.i18next.t('pmwjs_add_image_or_video_to_remove_bg'),
        type: GROWL_TYPE.NEUTRAL,
      });
    }
    setIsOpened(false);
  };

  const getPopupContent = (): ReactElement<any> => {
    return (
      <AddAiItemDropdownPopupContent
        showAsNewFeature={getNewFeatureMarkers()}
        dropdownContentState={dropdownContentState}
        onSubPanelBack={onSubPanelBack}
        onDone={onDone}
        onClickAiSubtitlesOption={onClickAiSubtitlesOption}
        onClickAiImagesOption={onClickAiImagesOption}
        onClickAiVoiceOption={onClickAiVoiceOption}
        onClickAiRemoveBgOption={onClickAiRemoveBgOption}
        AiImagePanelId={AI_IMAGE_PANEL_ID}
        AiVoicePanelId={AI_VOICE_PANEL_ID}
        AiSubtitlePanelId={AI_SUBTITLE_PANEL_ID}
        aiImageMediaType={aiImageMediaType}
      />
    );
  };

  const getClassesForPopup = (): string => {
    switch (dropdownContentState) {
      case AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN:
        return styles.aiPopup;
      case AddAiItemPopOverContentState.AI_IMAGE:
        return `${styles.aiImageContainerStyles} ${!numberOfGridItemsInAiImageGrid ? styles.emptyAiImageGridContent : ''}`;
      case AddAiItemPopOverContentState.AI_SUBTITLES:
        return `${styles.aiSubtitlesPanelPopupContainerStyles} ${getAiSubtitlesPanelHeightClass(aiSubtitleState, Object.values(aiSubtitleReduxState.aiSubtitlesPanel.items).length, aiSubtitleReduxState.generationsBatch.totalItemsToGenerateSubtitlesFor, !isPremium.current)}`;
      case AddAiItemPopOverContentState.AI_VOICE:
        return styles.aiVoiceContainerStyles;
      default:
        return '';
    }
  };

  const isFullHeightPopup = (): boolean => {
    if (dropdownContentState === AddAiItemPopOverContentState.AI_IMAGE && !!numberOfGridItemsInAiImageGrid) {
      return true;
    }
    if (dropdownContentState === AddAiItemPopOverContentState.AI_VOICE) {
      return true;
    }
    return false;
  };

  const shouldCloseOnOuterClick = (): boolean => {
    if (hasLanguageSelectorJustClosed) {
      setTimeout(() => {
        dispatch(setHasLanguageSelectorJustClosed(false));
      }, 10);

      return false;
    }

    return hasUserMediaPanelJustClosed === undefined;
  };

  const onAiVoicePopoverClose = (): void => {
    if (aiVoicePanelGenerationState === AiVoicePanelGenerationState.GENERATING) {
      showLoading(getLoadingKeyForAiVoice(AI_VOICE_PANEL_ID), {text: window.i18next.t('pmwjs_generating_audio')});
    }
  };

  const triggerCloseForContentState = (): void => {
    switch (dropdownContentState) {
      case AddAiItemPopOverContentState.AI_VOICE:
        onAiVoicePopoverClose();
        break;
      default:
        break;
    }
  };

  const onClose = (): void => {
    if (isOpened) {
      triggerCloseForContentState();
      setIsOpened(false);
      animatePopup.current = false;
      setAiImageMediaType(MediaTypePrompt.PHOTO);
      setDropdownContentState(AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN);
    }
  };

  const initAiVoiceNewStatus = async (): Promise<void> => {
    try {
      const response = await getHasUserSeenAiVoice();
      setShowAiVoiceAsNewFeature(!response);
    } catch {
      setShowAiVoiceAsNewFeature(false);
    }
  };

  const init = (): void => {
    void initAiVoiceNewStatus();
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <Dropdown
      doesCloseOnOuterClick={shouldCloseOnOuterClick()}
      open={isOpened}
      doCloseOnInnerClick={false}
      className="js-toolbar-text"
      id="add-ai-item-pop-over"
      key="toolbar-add-ai-item-pop-over"
      data-testid={props['data-testid']}
      alignment={isFullHeightPopup() ? ALIGNMENT_TYPE.RIGHT_START : ALIGNMENT_TYPE.RIGHT}
      selector={getSelector()}
      popup={getPopupContent()}
      position={DROPDOWN_POSITION.FIXED}
      popupOffset={ADD_ITEM_BAR_POPUP_OFFSET}
      topOffset={isFullHeightPopup() ? AI_PANEL_FULL_HEIGHT_POPUP_OFFSET : 0}
      arrowOffset={isFullHeightPopup() ? arrowOffset : 0}
      shouldShift={!isFullHeightPopup()}
      shouldFlip={!isFullHeightPopup()}
      popUpHasCustomHeight
      takeMaxHeight={isFullHeightPopup()}
      maxHeight={windowHeight - PADDING_AND_NAV_HEIGHT}
      popUpHasCustomWidth
      useDefaultArrow
      usePortal
      popupClassName={`${popupClassName} ${styles.aiDropdownPopup} ${animatePopup.current ? styles.popoverBaseStyle : ''} ${getClassesForPopup()}`}
      popupSpacingClass={dropdownContentState !== AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN ? 'spacing-p-0' : ''}
      arrowClasses={`${styles.arrowBaseStyle} ${arrowClasses}`}
      onOpen={(): void => {
        if (!isOpened) {
          setIsOpened(true);
        }
        setTimeout(() => {
          animatePopup.current = true;
        }, 500);
      }}
      onClose={onClose}
      isResumable
      fixedTopOffset={isFullHeightPopup() ? AI_PANEL_FULL_HEIGHT_POPUP_OFFSET : 0}
    />
  );
}
